import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  ArrowLeft,
  CreditCard,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Building2,
  Shield,
  Star
} from 'lucide-react';

interface PaymentMethod {
  _id: string;
  type: 'card' | 'bank' | 'paypal';
  name: string;
  details: {
    last4?: string;
    brand?: string;
    expiryMonth?: string;
    expiryYear?: string;
    bankName?: string;
    accountType?: string;
    email?: string;
  };
  isDefault: boolean;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};



const ManagePaymentMethodsPage: React.FC = ( ) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formType, setFormType] = useState<'card' | 'bank' | 'paypal'>('card');
  const API_BASE_URL = getApiBaseUrl();
  
  // Card form state
  const [cardForm, setCardForm] = useState({
    cardNumber: '',
    cardholderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    isDefault: false
  });
  
  // Bank form state
  const [bankForm, setBankForm] = useState({
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    bankName: '',
    accountType: 'checking',
    isDefault: false
  });
  
  // PayPal form state
  const [paypalForm, setPaypalForm] = useState({
    email: '',
    isDefault: false
  });

  // Add the refreshAccessToken function here
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        navigate('/login');
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        localStorage.clear();
        navigate('/login');
        return null;
      }

      const data = await response.json();
      localStorage.setItem('token', data.accessToken);
      return data.accessToken;
    } catch (error) {
      localStorage.clear();
      navigate('/login');
      return null;
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      
      // Try to get existing token or refresh it
      let token = localStorage.getItem('token');
      if (!token) {
        token = await refreshAccessToken();
        if (!token) return; // Will redirect to login
      }

      const response = await fetch(`${API_BASE_URL}/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // If token is expired, try to refresh
      if (response.status === 401) {
        token = await refreshAccessToken();
        if (!token) return; // Will redirect to login
        
        // Retry with new token
        const retryResponse = await fetch(`${API_BASE_URL}/payment-methods`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!retryResponse.ok) {
          throw new Error('Failed to fetch payment methods after token refresh');
        }
        
        const data = await retryResponse.json();
        setPaymentMethods(data.paymentMethods || []);
      } else if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data.paymentMethods || []);
      } else {
        throw new Error('Failed to fetch payment methods');
      }
      
      setError('');
    } catch (error: any) {
      setError('Failed to load payment methods. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get valid token
  const getValidToken = async () => {
    let token = localStorage.getItem('token');
    if (!token) {
      token = await refreshAccessToken();
    }
    return token;
  };

  const handleAddCard = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!cardForm.cardNumber || !cardForm.cardholderName || !cardForm.expiryMonth || !cardForm.expiryYear || !cardForm.cvv) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      const token = await getValidToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'card',
          details: {
            brand: cardForm.cardNumber.startsWith('4') ? 'visa' : 'mastercard',
            last4: cardForm.cardNumber.slice(-4),
            expiryMonth: cardForm.expiryMonth,
            expiryYear: cardForm.expiryYear
          },
          isDefault: cardForm.isDefault
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add card');
      }
      
      setSuccess('Card added successfully');
      setShowAddForm(false);
      setCardForm({
        cardNumber: '',
        cardholderName: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        isDefault: false
      });
      await fetchPaymentMethods();
    } catch (error: any) {
      setError(error.message || 'Failed to add card');
    }
  };

  const handleAddBank = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!bankForm.accountName || !bankForm.accountNumber || !bankForm.bankName) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      const token = await getValidToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'bank',
          details: {
            bankName: bankForm.bankName,
            last4: bankForm.accountNumber.slice(-4),
            accountType: bankForm.accountType
          },
          isDefault: bankForm.isDefault
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add bank account');
      }
      
      setSuccess('Bank account added successfully');
      setShowAddForm(false);
      setBankForm({
        accountName: '',
        accountNumber: '',
        routingNumber: '',
        bankName: '',
        accountType: 'checking',
        isDefault: false
      });
      await fetchPaymentMethods();
    } catch (error: any) {
      setError(error.message || 'Failed to add bank account');
    }
  };

  const handleAddPaypal = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!paypalForm.email) {
      setError('Please enter your PayPal email');
      return;
    }
    
    try {
      const token = await getValidToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'paypal',
          details: {
            email: paypalForm.email
          },
          isDefault: paypalForm.isDefault
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add PayPal account');
      }
      
      setSuccess('PayPal account added successfully');
      setShowAddForm(false);
      setPaypalForm({
        email: '',
        isDefault: false
      });
      await fetchPaymentMethods();
    } catch (error: any) {
      setError(error.message || 'Failed to add PayPal account');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const token = await getValidToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/payment-methods/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          isDefault: true
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to set default');
      }
      
      setSuccess('Default payment method updated');
      await fetchPaymentMethods();
    } catch (error: any) {
      setError(error.message || 'Failed to set default payment method');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const methodToDelete = paymentMethods.find(method => method._id === id);
      
      if (methodToDelete?.isDefault) {
        setError('Cannot delete default payment method. Set another method as default first.');
        return;
      }
      
      const token = await getValidToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/payment-methods/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete payment method');
      }
      
      setSuccess('Payment method deleted successfully');
      await fetchPaymentMethods();
    } catch (error: any) {
      setError(error.message || 'Failed to delete payment method');
    }
  };

  // Rest of your component code (getPaymentMethodIcon, getCardTypeIcon, etc.) remains the same...
  const getPaymentMethodIcon = (type: string) => {
    switch (type) {
      case 'card':
        return <CreditCard className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} />;
      case 'paypal':
        return (
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.067 8.478c.492.57.738 1.332.738 2.286 0 2.572-2.064 4.464-4.995 4.464h-1.584c-.245 0-.441.198-.486.443l-.411 2.59-.105.678a.436.436 0 01-.429.369h-2.436a.365.365 0 01-.36-.414l.023-.126.447-2.842.015-.078a.437.437 0 01.429-.369h1.35c2.931 0 5.22-1.892 5.896-4.536.201-.798.216-1.46.045-1.992a2.173 2.173 0 00-.147-.473z" fill="#009cde"/>
            <path d="M18.909 3.144C18.393 2.508 17.312 2.2 15.9 2.2H10.98c-.245 0-.453.18-.492.423L8.406 15.62l-.3.189a.365.365 0 00.36.414h2.616l.657-4.17-.021.132c.039-.243.246-.423.49-.423h1.023c2.01 0 3.582-1.305 4.041-3.114.015-.06.27-.126.039-.186.156-.84.06-1.413-.312-1.908a1.98 1.98 0 00-.39-.41z" fill="#012169"/>
            <path d="M10.488 8.478a.437.437 0 01.429-.369h2.724c.321 0 .624.021.9.066.078.012.156.027.231.045.075.018.15.039.222.063.036.012.072.024.108.036.144.051.279.111.405.18.492.57.738 1.332.738 2.286" fill="#003087"/>
          </svg>
         );
      case 'bank':
        return <Building2 className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} />;
      default:
        return <CreditCard className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} />;
    }
  };


  const getCardTypeIcon = (brand?: string) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return (
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.5 4.5H1.5C0.675 4.5 0 5.175 0 6V18C0 18.825 0.675 19.5 1.5 19.5H22.5C23.325 19.5 24 18.825 24 18V6C24 5.175 23.325 4.5 22.5 4.5Z" fill="#2566AF"/>
            <path d="M9.6 14.25L11.025 9.75H12.75L11.325 14.25H9.6Z" fill="white"/>
            <path d="M16.05 9.825C15.6 9.6 14.925 9.45 14.1 9.45C12.45 9.45 11.25 10.35 11.25 11.625C11.25 12.6 12.075 13.125 12.75 13.425C13.425 13.725 13.65 13.95 13.65 14.175C13.65 14.55 13.2 14.7 12.75 14.7C12.075 14.7 11.7 14.625 11.1 14.4L10.875 14.325L10.65 15.825C11.175 16.05 12 16.2 12.825 16.2C14.55 16.2 15.75 15.3 15.75 13.95C15.75 13.2 15.3 12.6 14.325 12.15C13.725 11.85 13.35 11.625 13.35 11.325C13.35 11.1 13.575 10.875 14.175 10.875C14.7 10.875 15.075 10.95 15.375 11.1L15.525 11.175L15.75 9.75L16.05 9.825Z" fill="white"/>
            <path d="M18.75 9.75H17.4C17.025 9.75 16.725 9.825 16.575 10.275L14.4 14.25H16.125C16.125 14.25 16.35 13.65 16.425 13.5H18.225C18.3 13.725 18.375 14.25 18.375 14.25H19.875L18.75 9.75ZM16.8 12.225C16.875 12 17.25 11.025 17.25 11.025C17.25 11.025 17.4 10.65 17.475 10.425L17.55 10.95C17.55 10.95 17.775 12.075 17.85 12.225H16.8Z" fill="white"/>
            <path d="M7.5 9.75L5.925 12.9L5.775 12.225C5.475 11.325 4.65 10.35 3.75 9.9L5.25 14.25H6.975L9.225 9.75H7.5Z" fill="white"/>
            <path d="M4.8 9.75H2.25L2.175 9.9C4.125 10.35 5.4 11.625 6 13.125L5.4 10.35C5.325 9.975 5.1 9.825 4.8 9.75Z" fill="#FAA61A"/>
          </svg>
        );
      case 'mastercard':
        return (
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.5 4.5H1.5C0.675 4.5 0 5.175 0 6V18C0 18.825 0.675 19.5 1.5 19.5H22.5C23.325 19.5 24 18.825 24 18V6C24 5.175 23.325 4.5 22.5 4.5Z" fill="#16366F"/>
            <path d="M9 16.5C11.4853 16.5 13.5 14.4853 13.5 12C13.5 9.51472 11.4853 7.5 9 7.5C6.51472 7.5 4.5 9.51472 4.5 12C4.5 14.4853 6.51472 16.5 9 16.5Z" fill="#D9222A"/>
            <path d="M15 16.5C17.4853 16.5 19.5 14.4853 19.5 12C19.5 9.51472 17.4853 7.5 15 7.5C12.5147 7.5 10.5 9.51472 10.5 12C10.5 14.4853 12.5147 16.5 15 16.5Z" fill="#EE9F2D"/>
            <path d="M12 7.5C13.1935 8.42 13.9749 9.81 13.9749 11.37C13.9749 12.93 13.1935 14.32 12 15.24C10.8065 14.32 10.0251 12.93 10.0251 11.37C10.0251 9.81 10.8065 8.42 12 7.5Z" fill="#D9222A"/>
          </svg>
        );
      case 'amex':
        return (
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.5 4.5H1.5C0.675 4.5 0 5.175 0 6V18C0 18.825 0.675 19.5 1.5 19.5H22.5C23.325 19.5 24 18.825 24 18V6C24 5.175 23.325 4.5 22.5 4.5Z" fill="#016FD0"/>
            <path d="M12.75 15H9.75V9H12.75L14.25 11.25L15.75 9H18.75V15H15.75V12L14.25 14.25L12.75 12V15Z" fill="white"/>
            <path d="M5.25 9H9.75L10.5 10.5L11.25 9H15.75L13.5 12L15.75 15H11.25L10.5 13.5L9.75 15H5.25V9Z" fill="white"/>
          </svg>
        );
      default:
        return <CreditCard className="w-8 h-8 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className={`h-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'} rounded-lg animate-pulse`}></div>
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6 rounded-lg shadow-sm border animate-pulse`}>
              <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-3/4 mb-4`}></div>
              <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-full mb-2`}></div>
              <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-2/3`}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/settings"
            className={`p-2 ${theme === 'dark' ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-colors duration-200`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Payment Methods</h1>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
              Manage your payment methods
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setShowAddForm(true);
            setFormType('card');
            setError('');
            setSuccess('');
          }}
          className={`flex items-center px-4 py-2 ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors duration-200`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Payment Method
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
        } border rounded-lg p-4`}>
          <div className="flex items-center">
            <AlertCircle className={`w-5 h-5 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'} mr-2`} />
            <span className={`${theme === 'dark' ? 'text-red-300' : 'text-red-700'} text-sm`}>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className={`${
          theme === 'dark' ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'
        } border rounded-lg p-4`}>
          <div className="flex items-center">
            <CheckCircle className={`w-5 h-5 ${theme === 'dark' ? 'text-green-400' : 'text-green-500'} mr-2`} />
            <span className={`${theme === 'dark' ? 'text-green-300' : 'text-green-700'} text-sm`}>{success}</span>
          </div>
        </div>
      )}

      {/* Add Payment Method Form */}
      {showAddForm && (
        <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-sm border`}>
          <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Add Payment Method
            </h2>
          </div>
          
          <div className="p-6">
            {/* Payment Method Type Selector */}
            <div className="mb-6">
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Payment Method Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={() => setFormType('card')}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                    formType === 'card'
                      ? theme === 'dark' 
                        ? 'border-blue-500 bg-blue-900/20 text-blue-400' 
                        : 'border-blue-500 bg-blue-50 text-blue-700'
                      : theme === 'dark'
                        ? 'border-gray-700 hover:border-gray-600'
                        : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <CreditCard className="w-6 h-6 mb-2" />
                  <div className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>Credit/Debit Card</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Add a new card for payments</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormType('bank')}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                    formType === 'bank'
                      ? theme === 'dark' 
                        ? 'border-blue-500 bg-blue-900/20 text-blue-400' 
                        : 'border-blue-500 bg-blue-50 text-blue-700'
                      : theme === 'dark'
                        ? 'border-gray-700 hover:border-gray-600'
                        : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Building2 className="w-6 h-6 mb-2" />
                  <div className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>Bank Account</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Link your bank account</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormType('paypal')}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                    formType === 'paypal'
                      ? theme === 'dark' 
                        ? 'border-blue-500 bg-blue-900/20 text-blue-400' 
                        : 'border-blue-500 bg-blue-50 text-blue-700'
                      : theme === 'dark'
                        ? 'border-gray-700 hover:border-gray-600'
                        : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <svg className="w-6 h-6 mb-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.067 8.478c.492.57.738 1.332.738 2.286 0 2.572-2.064 4.464-4.995 4.464h-1.584c-.245 0-.441.198-.486.443l-.411 2.59-.105.678a.436.436 0 01-.429.369h-2.436a.365.365 0 01-.36-.414l.023-.126.447-2.842.015-.078a.437.437 0 01.429-.369h1.35c2.931 0 5.22-1.892 5.896-4.536.201-.798.216-1.46.045-1.992a2.173 2.173 0 00-.147-.473z" fill="#009cde"/>
                    <path d="M18.909 3.144C18.393 2.508 17.312 2.2 15.9 2.2H10.98c-.245 0-.453.18-.492.423L8.406 15.62l-.3.189a.365.365 0 00.36.414h2.616l.657-4.17-.021.132c.039-.243.246-.423.49-.423h1.023c2.01 0 3.582-1.305 4.041-3.114.015-.06.27-.126.039-.186.156-.84.06-1.413-.312-1.908a1.98 1.98 0 00-.39-.41z" fill="#012169"/>
                    <path d="M10.488 8.478a.437.437 0 01.429-.369h2.724c.321 0 .624.021.9.066.078.012.156.027.231.045.075.018.15.039.222.063.036.012.072.024.108.036.144.051.279.111.405.18.492.57.738 1.332.738 2.286" fill="#003087"/>
                  </svg>
                  <div className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>PayPal</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Pay with your PayPal account</div>
                </button>
              </div>
            </div>
            
            {/* Credit Card Form */}
            {formType === 'card' && (
              <form onSubmit={handleAddCard} className="space-y-4">
                <div>
                  <label htmlFor="cardNumber" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Card Number *
                  </label>
                  <input
                    type="text"
                    id="cardNumber"
                    value={cardForm.cardNumber}
                    onChange={(e) => setCardForm({...cardForm, cardNumber: e.target.value})}
                    placeholder="1234 5678 9012 3456"
                    className={`w-full px-3 py-2 border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="cardholderName" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Cardholder Name *
                  </label>
                  <input
                    type="text"
                    id="cardholderName"
                    value={cardForm.cardholderName}
                    onChange={(e) => setCardForm({...cardForm, cardholderName: e.target.value})}
                    placeholder="John Doe"
                    className={`w-full px-3 py-2 border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="expiryDate" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Expiry Date *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        id="expiryMonth"
                        value={cardForm.expiryMonth}
                        onChange={(e) => setCardForm({...cardForm, expiryMonth: e.target.value})}
                        className={`px-3 py-2 border ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        required
                      >
                        <option value="">Month</option>
                        {Array.from({ length: 12 }, (_, i) => {
                          const month = i + 1;
                          return (
                            <option key={month} value={month.toString().padStart(2, '0')}>
                              {month.toString().padStart(2, '0')}
                            </option>
                          );
                        })}
                      </select>
                      
                      <select
                        id="expiryYear"
                        value={cardForm.expiryYear}
                        onChange={(e) => setCardForm({...cardForm, expiryYear: e.target.value})}
                        className={`px-3 py-2 border ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        required
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 10 }, (_, i) => {
                          const year = new Date().getFullYear() + i;
                          return (
                            <option key={year} value={year.toString()}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="cvv" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      CVV *
                    </label>
                    <input
                      type="text"
                      id="cvv"
                      value={cardForm.cvv}
                      onChange={(e) => setCardForm({...cardForm, cvv: e.target.value})}
                      placeholder="123"
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      required
                    />
                  </div>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="defaultCard"
                    checked={cardForm.isDefault}
                    onChange={(e) => setCardForm({...cardForm, isDefault: e.target.checked})}
                    className={`h-4 w-4 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-blue-600'
                        : 'bg-white border-gray-300 text-blue-600'
                    } rounded focus:ring-blue-500`}
                  />
                  <label htmlFor="defaultCard" className={`ml-2 block text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Set as default payment method
                  </label>
                </div>
                
                <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className={`px-4 py-2 ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } rounded-lg transition-colors duration-200`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 ${
                        theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
                      } text-white rounded-lg transition-colors duration-200`}
                    >
                      Add Card
                    </button>
                  </div>
                </div>
              </form>
            )}
            
            {/* Bank Account Form */}
            {formType === 'bank' && (
              <form onSubmit={handleAddBank} className="space-y-4">
                <div>
                  <label htmlFor="accountName" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Account Holder Name *
                  </label>
                  <input
                    type="text"
                    id="accountName"
                    value={bankForm.accountName}
                    onChange={(e) => setBankForm({...bankForm, accountName: e.target.value})}
                    placeholder="John Doe"
                    className={`w-full px-3 py-2 border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="bankName" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    id="bankName"
                    value={bankForm.bankName}
                    onChange={(e) => setBankForm({...bankForm, bankName: e.target.value})}
                    placeholder="Chase Bank"
                    className={`w-full px-3 py-2 border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="accountNumber" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Account Number *
                    </label>
                    <input
                      type="text"
                      id="accountNumber"
                      value={bankForm.accountNumber}
                      onChange={(e) => setBankForm({...bankForm, accountNumber: e.target.value})}
                      placeholder="123456789"
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="routingNumber" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Routing Number
                    </label>
                    <input
                      type="text"
                      id="routingNumber"
                      value={bankForm.routingNumber}
                      onChange={(e) => setBankForm({...bankForm, routingNumber: e.target.value})}
                      placeholder="987654321"
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="accountType" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Account Type
                  </label>
                  <select
                    id="accountType"
                    value={bankForm.accountType}
                    onChange={(e) => setBankForm({...bankForm, accountType: e.target.value})}
                    className={`w-full px-3 py-2 border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="defaultBank"
                    checked={bankForm.isDefault}
                    onChange={(e) => setBankForm({...bankForm, isDefault: e.target.checked})}
                    className={`h-4 w-4 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-blue-600'
                        : 'bg-white border-gray-300 text-blue-600'
                    } rounded focus:ring-blue-500`}
                  />
                  <label htmlFor="defaultBank" className={`ml-2 block text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Set as default payment method
                  </label>
                </div>
                
                <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className={`px-4 py-2 ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } rounded-lg transition-colors duration-200`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 ${
                        theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
                      } text-white rounded-lg transition-colors duration-200`}
                    >
                      Add Bank Account
                    </button>
                  </div>
                </div>
              </form>
            )}
            
            {/* PayPal Form */}
            {formType === 'paypal' && (
              <form onSubmit={handleAddPaypal} className="space-y-4">
                <div>
                  <label htmlFor="paypalEmail" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    PayPal Email *
                  </label>
                  <input
                    type="email"
                    id="paypalEmail"
                    value={paypalForm.email}
                    onChange={(e) => setPaypalForm({...paypalForm, email: e.target.value})}
                    placeholder="your-email@example.com"
                    className={`w-full px-3 py-2 border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="defaultPaypal"
                    checked={paypalForm.isDefault}
                    onChange={(e) => setPaypalForm({...paypalForm, isDefault: e.target.checked})}
                    className={`h-4 w-4 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-blue-600'
                        : 'bg-white border-gray-300 text-blue-600'
                    } rounded focus:ring-blue-500`}
                  />
                  <label htmlFor="defaultPaypal" className={`ml-2 block text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Set as default payment method
                  </label>
                </div>
                
                <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className={`px-4 py-2 ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } rounded-lg transition-colors duration-200`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 ${
                        theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
                      } text-white rounded-lg transition-colors duration-200`}
                    >
                      Add PayPal
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Payment Methods List */}
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-sm border`}>
        <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Your Payment Methods
          </h2>
        </div>
        
        <div className="p-6">
          {paymentMethods.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className={`w-16 h-16 ${theme === 'dark' ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>No payment methods</h3>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
                Add a payment method to make payments easier.
              </p>
              <button
                onClick={() => {
                  setShowAddForm(true);
                  setFormType('card');
                }}
                className={`inline-flex items-center px-4 py-2 ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors duration-200`}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Payment Method
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div 
                  key={method._id} 
                  className={`p-4 border ${
                    method.isDefault 
                      ? theme === 'dark'
                        ? 'border-blue-500 bg-blue-900/10'
                        : 'border-blue-500 bg-blue-50'
                      : theme === 'dark'
                        ? 'border-gray-700'
                        : 'border-gray-200'
                  } rounded-lg`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {method.type === 'card' ? (
                        getCardTypeIcon(method.details.brand)
                      ) : (
                        getPaymentMethodIcon(method.type)
                      )}
                      
                      <div>
                        <div className="flex items-center">
                          <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {method.name}
                          </h3>
                          {method.isDefault && (
                            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                              theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                            }`}>
                              Default
                            </span>
                          )}
                        </div>
                        
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {method.type === 'card' && (
                            <>Expires {method.details.expiryMonth}/{method.details.expiryYear}</>
                          )}
                          {method.type === 'paypal' && (
                            <>{method.details.email}</>
                          )}
                          {method.type === 'bank' && (
                            <>Account ending in {method.details.last4}</>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!method.isDefault && (
                        <button
                          onClick={() => handleSetDefault(method._id)}
                          className={`p-2 ${
                            theme === 'dark'
                              ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                          } rounded-lg transition-colors duration-200`}
                          title="Set as default"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(method._id)}
                        className={`p-2 ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700'
                            : 'text-gray-400 hover:text-red-600 hover:bg-gray-100'
                        } rounded-lg transition-colors duration-200`}
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Security Information */}
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-6`}>
        <div className="flex items-start space-x-4">
          <div className={`p-3 rounded-full ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
            <Shield className={`w-6 h-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
              Your payment information is secure
            </h3>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              We use industry-standard encryption to protect your payment information. Your card details are never stored on our servers and are securely processed by our payment provider.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagePaymentMethodsPage;